import { Component, DestroyRef, OnInit, Input, EventEmitter, Output, inject } from '@angular/core';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormGroup } from '@angular/forms';
import { CartService } from '../../../services/cart/cart.service';
import { ShoppingCart } from '../../../services/cart/shopping-cart';
import { LookupService, OrganizationService } from '../../../services/rnapi2-service/apis/api';
import {
  RnOrganizationPaymentOnFileVM,
  RnOrganizationsProfileVM
} from '../../../services/rnapi2-service/models/models';
import { SelectionService } from '../../../services/selection/selection.service';
import { OrganizationLogicService } from '../../../services/organization-logic/organization-logic.service';
import { ToasterService, ToastType } from '@rn-platform/frontend-shared-data-access-toaster';

@Component({
  selector: 'app-review-user',
  templateUrl: './review-user.component.html',
  styleUrls: ['./review-user.component.scss'],
  standalone: false
})
export class ReviewUserComponent implements OnInit {
  private cartService = inject(CartService);
  private selectionService = inject(SelectionService);
  private toasterService = inject(ToasterService);
  private lookupService = inject(LookupService);
  private organizationService = inject(OrganizationService);
  private orgLogicService = inject(OrganizationLogicService);
  private readonly destroyRef = inject(DestroyRef);

  public shoppingCart: ShoppingCart;
  public selectedOrg: RnOrganizationsProfileVM = new RnOrganizationsProfileVM();
  public inputPromoCode = '';
  public promoCodeIsValid: boolean;
  public wait: boolean;
  public error: boolean;
  public nextBillTotal = 0;
  public nextBillAmount = 0;
  public recalculatingTotal: boolean;
  public futureMonthlyBill: number;

  public hasDiscounts: boolean;
  public canShowPromoCodeEntry = false;

  private beforePromoApplyShoppingCart = {};
  private initialAmountDue;
  public validatedPromo: any = {};
  private truncateSize = 60;

  @Input() reviewForm: FormGroup;
  @Input() paymentInfo: RnOrganizationPaymentOnFileVM;
  @Output() promoEvent = new EventEmitter<any>();

  ngOnInit(): void {
    this.initialAmountDue = this.paymentInfo?.NextBillAmount;
    this.cartService.getCart().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((response) => {
      this.shoppingCart = response;
      this.futureMonthlyBill =
        Number.parseFloat(this.paymentInfo?.NextBillAmount) >= 0
          ? Number.parseFloat(this.paymentInfo?.NextBillAmount) + this.shoppingCart.CartTotal
          : this.nextBillAmount + this.nextBillTotal;
    })

    this.selectionService.SelectedOrgProfile.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((response) => {
      this.selectedOrg = response;
    })

    if (this.cartService.hasDiscounts() && this.validatedPromo && this.validatedPromo.promoCodeIsValid) {
      this.nextBillTotal = this.cartService.updateTotal(this.validatedPromo);
    }
    this.canShowPromoCodeEntry = this.orgLogicService.CanShowPromoCodeEntry();
    this.hasDiscounts = this.cartService.hasDiscounts();
  }

  getTooltip(): string {
    return this.truncateSize && this.email?.value.length >= this.truncateSize ? this.email?.value : '';
  }

  getTruncated(): string {
    const slicedEmail: string = this.email?.value.slice(0, this.truncateSize - 1);
    return this.truncateSize && this.email?.value.length > this.truncateSize ? slicedEmail + '...' : this.email?.value;
  }

  public validatePromoCode(code) {
    if (this.wait == true || this.promoCodeIsValid == true) {
      return;
    }

    this.wait = true;
    this.promoCodeIsValid = false;
    this.error = false;
    this.validatedPromo = {};
    this.inputPromoCode = this.inputPromoCode.toUpperCase();

    //get package ids to validate
    const packageIDs = this.cartService.getAllIds();
    const orgIdForPromoValidation = this.selectedOrg?.IdThatPaysForThisOrg ?? this.selectedOrg?.ID;

    this.lookupService.apiV2LookupValidatepromocodeGet(code, packageIDs, orgIdForPromoValidation, 'response').subscribe({
      next: (response) => {
        const promoValidationResponse = response?.body?.data;

        if (promoValidationResponse) {
          this.validatedPromo = promoValidationResponse;
          this.promoEvent.emit(this.validatedPromo);
          this.wait = false;

          //good code
          if (this.validatedPromo && this.validatedPromo.promoCodeIsValid) {
            this.promoCodeIsValid = true;

            this.beforePromoApplyShoppingCart = this.cartService.cart;

            this.nextBillTotal = this.cartService.updateTotal(this.validatedPromo);
            this.getNextBalanceForShoppingCart();
          }
          //code not allowed
          else {
            this.error = true;
          }
        }
        //not successful - something like timeout or 500
        else {
          this.invalidatePromoCode(response);
        }
      },
      //just in case
      error: (error) => {
        this.invalidatePromoCode(error);
      }
    });
  }

  private invalidatePromoCode(err: unknown) {
    this.wait = false;
    this.promoCodeIsValid = false;
    this.validatedPromo = {};
    this.promoEvent.emit(this.validatedPromo);
    const content = 'There was a problem checking the promo code. Please try again.';
    const id = 'apphub-review-user-promo-error-toast';
    this.toasterService.addToast(ToastType.Error_, content, content, id, id);
  }

  private getNextBalanceForShoppingCart() {
    this.recalculatingTotal = true;
    this.organizationService
      .apiV2OrganizationsCalculateOrgBalanceNextDuePost(this.selectedOrg.ID, this.validatedPromo.promoCode?.ID)
      .subscribe({
        next: (response) => {
          if (response && response.data && response.Success) {
            this.nextBillAmount = Math.round(Number.parseFloat(response.data) * 100) / 100;
            this.cartService.calculateTotal();
          } else {
            const content = 'There was an error calculating the next bill amount';
            const id = 'apphub-review-user-calculate-balance-error-toast';
            this.toasterService.addToast(ToastType.Error_, content, content, id, id);
            let errMsg = content;
            if (response && response.data && response.Messages && response.Messages.length > 0) {
              for (let i = 0; i < response.Messages.length; i++) {
                const error = response.Messages[i];
                errMsg +=
                  '\r\nCode: ' + error.Code + '\r\nName: ' + error.Name + '\r\nDescription: ' + error.Description;
              }
            }
            console.log(errMsg);
          }
          this.recalculatingTotal = false;
        },
        error: (error) => {
          console.error(error);
          const content = 'Calculating the next bill amount resulted in an error';
          const id = 'apphub-review-user-calculate-balance-error-toast';
          this.toasterService.addToast(ToastType.Error_, content, content, id, id);
          this.recalculatingTotal = false;
        }
      });
  }

  public onPromoChange() {
    //if promocode was validated then got updated, then promo needs revalidation
    if (this.validatedPromo.promoCode != undefined && this.validatedPromo.promoCode?.PromoCode != this.inputPromoCode) {
      this.clearPromo();
    }
  }

  private clearPromo() {
    this.promoCodeIsValid = false;
    this.validatedPromo = {};
    this.nextBillTotal = this.cartService.updateTotal(this.validatedPromo);
  }

  public showFutureMonthlyBill() {
    return this.cartService.getItems().length > 0 || Number.parseFloat(this.paymentInfo?.NextBillAmount) >= 0;
  }

  public getFutureMonthlyBill() {
    return Number.parseFloat(this.paymentInfo?.NextBillAmount) >= 0
      ? Number.parseFloat(this.paymentInfo?.NextBillAmount) + this.shoppingCart.CartMonthlyTotal
      : this.nextBillAmount + this.nextBillTotal;
  }

  get userDetailsForm() {
    return this.reviewForm?.get('UserCreate') as FormGroup;
  }
  get firstName() {
    return this.userDetailsForm.get('firstName');
  }
  get lastName() {
    return this.userDetailsForm.get('lastName');
  }
  get email() {
    return this.userDetailsForm.get('email');
  }
  get role() {
    return this.userDetailsForm.get('role');
  }
  get packageFormData() {
    return this.reviewForm.get('UserPackage') as FormGroup;
  }
  get selectedPackage() {
    return this.packageFormData?.get('selectedPackage');
  }
  get UserAddons() {
    return this.reviewForm.get('UserAddons') as FormGroup;
  }
  get selectedAddons() {
    return this.UserAddons?.get('selectedAddons').value;
  }
}
